import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Icon } from '../components/icons';
import { Avatar, Badge, Button, Card, ErrorNote, Field, Input, Loading, PageHeader, ProgressBar, Segmented, Select, SkeletonCard, TD, TH, TR, cx } from '../components/ui';
import { useTheme, type Theme } from '../lib/theme';
import { useAuth } from '../lib/auth';
import { api, type OrgBilling, type SettingsStatus, type User } from '../lib/api';
import { formatMoney, monthName } from '../lib/format';

const DAYS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function SettingsPage() {
  const { user } = useAuth();
  const status = useQuery({ queryKey: ['settings'], queryFn: () => api.get<SettingsStatus>('/api/settings/status') });
  const s = status.data;
  return (
    <>
      <PageHeader eyebrow="Settings" title={user?.orgName ?? 'Workspace'} subtitle="Your company's workspace: your plan and bills, when the AI may call, and who is on the team." />
      {status.isLoading && <Loading />}
      <ErrorNote error={status.error} />
      <div className="space-y-5">
        <Billing />
        <div className="grid gap-5 lg:grid-cols-2">
          {s && (
            <Card title="Calls and interviews" description="When the AI may ring candidates.">
              <Rows
                rows={[
                  ['Calling hours', s.calling.window],
                  ['Days', s.calling.days.split(',').map((d) => DAYS[Number(d)]).join(', ')],
                  ['Interviews in progress', String(s.liveSessions)],
                ]}
              />
            </Card>
          )}
          <Appearance />
          {user?.role === 'ADMIN' && <CompanyName />}
        </div>
        {user?.role === 'ADMIN' && <Users />}
      </div>
    </>
  );
}

/** The company's plan, this month's usage against it, and its invoices. */
function Billing() {
  const { user } = useAuth();
  const q = useQuery({ queryKey: ['org', 'billing'], queryFn: () => api.get<OrgBilling>('/api/org/billing') });
  const [email, setEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const b = q.data;
  if (q.isLoading) return <SkeletonCard lines={3} />;
  if (!b) return <ErrorNote error={q.error} />;
  const m = b.month;
  const pct = m.includedInterviews ? Math.min(100, (m.interviews / m.includedInterviews) * 100) : 0;
  const unpaid = b.invoices.filter((i) => i.status === 'ISSUED');
  const current = email ?? b.billingEmail ?? '';
  return (
    <Card title="Plan and billing" description={`Your ${b.plan.planName} plan, and ${monthName(b.period.year, b.period.month)} so far.`} actions={<Badge tone={unpaid.length ? 'amber' : 'green'} dot>{unpaid.length ? `${unpaid.length} invoice${unpaid.length > 1 ? 's' : ''} to pay` : 'Nothing owed'}</Badge>}>
      <div className="grid gap-5 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-md bg-surface-2 p-3.5 ring-1 ring-border">
              <p className="eyebrow">Interviews</p>
              <p className="display mt-2 text-[28px] font-medium leading-none tabular text-fg">{m.interviews}</p>
              <p className="mt-1.5 text-xs text-muted-fg">{m.includedInterviews ? `of ${m.includedInterviews} included` : 'pay as you go'}</p>
            </div>
            <div className="rounded-md bg-surface-2 p-3.5 ring-1 ring-border">
              <p className="eyebrow">Minutes</p>
              <p className="display mt-2 text-[28px] font-medium leading-none tabular text-fg">{m.minutes}</p>
              <p className="mt-1.5 text-xs text-muted-fg">of AI interview time</p>
            </div>
            <div className="rounded-md bg-surface-2 p-3.5 ring-1 ring-border">
              <p className="eyebrow">This month</p>
              <p className="display mt-2 text-[28px] font-medium leading-none tabular text-brand-gradient">{formatMoney(m.total)}</p>
              <p className="mt-1.5 text-xs text-muted-fg">
                {formatMoney(m.monthlyFee)} plan{m.usageFee ? ` + ${formatMoney(m.usageFee)} for ${m.extraInterviews} extra` : ''}
              </p>
            </div>
          </div>
          {m.includedInterviews > 0 && (
            <div className="mt-4">
              <ProgressBar value={pct} tone={m.extraInterviews ? 'warning' : 'brand'} />
              <p className="mt-1.5 text-xs text-muted-fg">
                {m.extraInterviews ? `${m.extraInterviews} beyond the plan at ${formatMoney(b.plan.pricePerInterview)} each.` : `${m.includedInterviews - m.interviews} interviews left in the plan this month.`}
              </p>
            </div>
          )}
          <p className="mt-4 text-xs text-muted-fg">Each AI interview or skills assessment that was held counts once. Invoices are raised at the end of the month and sent to the billing email.</p>
          {user?.role === 'ADMIN' && (
            <form
              className="mt-4 flex flex-wrap items-end gap-2"
              onSubmit={async (e) => {
                e.preventDefault();
                setBusy(true);
                setError(null);
                try {
                  await api.patch('/api/org', { billingEmail: current.trim() || null });
                  await q.refetch();
                  setEmail(null);
                } catch (err) {
                  setError(err);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Field label="Billing email" className="min-w-[240px] flex-1">
                <Input type="email" value={current} onChange={(e) => setEmail(e.target.value)} leading={<Icon name="mail" className="h-4 w-4" />} placeholder="accounts@company.com" />
              </Field>
              <Button type="submit" loading={busy} disabled={email === null || email === (b.billingEmail ?? '')}>
                Save
              </Button>
              <div className="basis-full">
                <ErrorNote error={error} />
              </div>
            </form>
          )}
        </div>
        <div>
          <p className="mb-2 text-[13px] font-semibold text-fg">Invoices</p>
          {b.invoices.length === 0 ? (
            <p className="text-sm text-muted-fg">None yet.</p>
          ) : (
            <ul className="divide-y divide-border overflow-hidden rounded-md ring-1 ring-border">
              {b.invoices.map((i) => {
                const d = new Date(i.periodStart);
                return (
                  <li key={i.id} className="flex items-center gap-3 bg-surface px-3 py-2.5 text-sm">
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-fg">{monthName(d.getUTCFullYear(), d.getUTCMonth() + 1)}</span>
                      <span className="block font-mono text-[11px] text-muted-fg">
                        {i.number} · {i.interviews} interviews
                      </span>
                    </span>
                    <span className="font-mono text-sm tabular text-fg">{formatMoney(i.total)}</span>
                    <Badge tone={i.status === 'PAID' ? 'green' : 'amber'} size="xs">
                      {i.status === 'PAID' ? 'paid' : 'due'}
                    </Badge>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}

function CompanyName() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.orgName ?? '');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Card title="Company" description="Shown to your team. New jobs start with it as the company the interviewer speaks for.">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await api.patch('/api/org', { name });
            window.location.reload(); // the name is part of the session shown across the app
          } catch (err) {
            setError(err);
            setBusy(false);
          }
        }}
      >
        <Field label="Name" className="min-w-[220px] flex-1">
          <Input required minLength={2} maxLength={120} value={name} leading={<Icon name="building" className="h-4 w-4" />} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" loading={busy} disabled={name.trim() === user?.orgName}>
          Save
        </Button>
      </form>
      <ErrorNote error={error} className="mt-3" />
    </Card>
  );
}

function Appearance() {
  const { theme, setTheme } = useTheme();
  return (
    <Card title="Appearance" description="Theme for this browser. The live wall is always dark.">
      <Segmented<Theme>
        label="Theme"
        size="md"
        value={theme}
        onChange={setTheme}
        options={[
          { value: 'light', label: 'Light', icon: 'sun' },
          { value: 'dark', label: 'Dark', icon: 'moon' },
        ]}
      />
    </Card>
  );
}

function Rows({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="divide-y divide-border text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between gap-4 py-2 first:pt-0 last:pb-0">
          <dt className="text-muted-fg">{k}</dt>
          <dd className="text-right font-mono text-xs text-fg">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

type Member = Pick<User, 'id' | 'email' | 'name' | 'role'> & { active: boolean };

function Users() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<Member[]>('/api/users') });
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'RECRUITER' });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState<string | null>(null);

  const change = async (u: Member, patch: Partial<Pick<Member, 'role' | 'active'>>) => {
    setChanging(u.id);
    setError(null);
    try {
      await api.patch(`/api/users/${u.id}`, patch);
      await qc.invalidateQueries({ queryKey: ['users'] });
    } catch (err) {
      setError(err);
    } finally {
      setChanging(null);
    }
  };

  return (
    <Card title="Team" description="Everyone here sees your company's jobs and candidates, and nobody else's. Admins also manage the team. Switch an account off when someone leaves: they are signed out at once." padding="none">
      <div className="px-5 pb-5 sm:px-6">
        <div className="overflow-x-auto rounded-md ring-1 ring-border">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-surface-2">
              <tr>
                <th className={TH}>Person</th>
                <th className={TH}>Role</th>
                <th className={cx(TH, 'text-right')}>Access</th>
              </tr>
            </thead>
            <tbody>
              {users.data?.map((u) => (
                <tr key={u.id} className={TR}>
                  <td className={TD}>
                    <div className="flex items-center gap-2.5">
                      <Avatar name={u.name} size="sm" className={cx(!u.active && 'opacity-40')} />
                      <div className="min-w-0">
                        <div className={cx('font-semibold', u.active ? 'text-fg' : 'text-muted-fg line-through')}>
                          {u.name}
                          {u.id === me?.id && <span className="ml-1.5 font-normal text-muted-fg">(you)</span>}
                        </div>
                        <div className="text-xs text-muted-fg">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className={TD}>
                    {u.id === me?.id ? (
                      <Badge tone="indigo">{u.role.toLowerCase()}</Badge>
                    ) : (
                      <span className="block w-36">
                        <Select aria-label={`Role of ${u.name}`} value={u.role} disabled={changing === u.id} onChange={(e) => change(u, { role: e.target.value as Member['role'] })}>
                          <option value="RECRUITER">Recruiter</option>
                          <option value="ADMIN">Admin</option>
                        </Select>
                      </span>
                    )}
                  </td>
                  <td className={cx(TD, 'text-right')}>
                    {u.id === me?.id ? (
                      <Badge tone="green" dot>
                        on
                      </Badge>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        {!u.active && (
                          <Badge tone="amber" dot>
                            switched off
                          </Badge>
                        )}
                        <Button size="sm" loading={changing === u.id} onClick={() => change(u, { active: !u.active })}>
                          {u.active ? 'Switch off' : 'Switch on'}
                        </Button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form
          className="mt-5 grid gap-3 rounded-md bg-surface-2 p-4 ring-1 ring-border md:grid-cols-5"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              await api.post('/api/users', form);
              setForm({ name: '', email: '', password: '', role: 'RECRUITER' });
              await qc.invalidateQueries({ queryKey: ['users'] });
            } catch (err) {
              setError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          <p className="text-sm font-semibold text-fg md:col-span-5">Add someone</p>
          <Field label="Name">
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Email">
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Temporary password">
            <Input required minLength={10} type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
          <Field label="Role">
            <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="RECRUITER">Recruiter</option>
              <option value="ADMIN">Admin</option>
            </Select>
          </Field>
          <div className="flex items-end">
            <Button type="submit" variant="primary" icon="plus" loading={busy} className="w-full">
              Add member
            </Button>
          </div>
          <div className="md:col-span-5">
            <ErrorNote error={error} />
          </div>
        </form>
      </div>
    </Card>
  );
}
